import React from 'react';
import { LucideIcon, TrendingUp, Trash2, Calculator, Handshake, LineChart, Package, Wine } from 'lucide-react';
import { CostPriceAnalysis } from '../../../../services/costPrice/costPriceService';
import { COST_PRICE_TYPE_CHOICE } from '../../../../utils/costPriceUtils';
import OverviewReport from './OverviewReport';
import CostPriceReport from './CostPriceReport';
import UtilizationReport from './UtilizationReport';
import SupplierReport from './SupplierReport';
import InflationReport from './InflationReport';
import InventoryReport from './InventoryReport';
import ConsumptionReport from './ConsumptionReport';

/**
 * Data the hub uses to decide which report cards are available: inventory counts
 * gate R6 (Inventory) and R7 (True Consumption), cost prices gate R2 (Utilization)
 * and R3 (Cost Price & Margin).
 */
export interface ReportAvailabilityContext {
  inventorySessionsCount: number;
  /** True when at least one group or section has ≥2 counts to compare (gates R7). */
  hasConsumptionWindow: boolean;
  /** The restaurant's cost price analyses (gates R2 + R3). */
  costPriceList: CostPriceAnalysis[];
}

export interface ReportDefinition {
  /** URL key: `/reports/:reportKey`. */
  key: string;
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  /** Whether the report's data prerequisites are met for the current restaurant. */
  isAvailable: (ctx: ReportAvailabilityContext) => boolean;
  /** When unavailable: true → hide the card entirely; false → show a disabled card
   * with `unavailableHintKey`. */
  hideWhenUnavailable?: boolean;
  unavailableHintKey?: string;
  /** Whether this report produces meaningful combined numbers in "all restaurants"
   * mode. Order/revenue-driven reports (overview, supplier, inflation) aggregate
   * cleanly across restaurants → true. Cost-price/inventory-driven reports rely on
   * per-restaurant sourcing/recipes/counts that aren't loaded (and are ambiguous to
   * merge across restaurants) → false: they show a "pick a single restaurant" notice. */
  supportsAllRestaurants?: boolean;
  /** When set, the shared `TopSection` shows the overall-type filter (food/drinks/…)
   * for this report, driven by the shared `useFiltersStore` — same control as the
   * order/revenue pages. Omitted → no type filter. */
  overallTypeOptions?: string[];
  /** Report body rendered inside `ReportDetailLayout`. */
  Component: React.FC;
}

/**
 * The v1 report catalog. Order here is the order shown on the hub.
 */
export const REPORTS: ReportDefinition[] = [
  {
    key: 'overview',
    icon: TrendingUp,
    titleKey: 'reports.catalog.overview.title',
    descriptionKey: 'reports.catalog.overview.description',
    isAvailable: () => true,
    supportsAllRestaurants: true,
    Component: OverviewReport,
  },
  {
    key: 'utilization',
    supportsAllRestaurants: true,
    icon: Trash2,
    titleKey: 'reports.catalog.utilization.title',
    descriptionKey: 'reports.catalog.utilization.description',
    // R2 needs ≥1 cost price linked to a menu item (CHOICE rows are never dishes).
    isAvailable: (ctx) =>
      ctx.costPriceList.some(
        (cp) => !!cp.menuItemName && cp.costPriceType !== COST_PRICE_TYPE_CHOICE
      ),
    unavailableHintKey: 'reports.catalog.utilization.unavailable',
    overallTypeOptions: ['all', 'food', 'drinks'],
    Component: UtilizationReport,
  },
  {
    key: 'costprice',
    icon: Calculator,
    titleKey: 'reports.catalog.costprice.title',
    descriptionKey: 'reports.catalog.costprice.description',
    // R3 needs ≥1 cost price item.
    isAvailable: (ctx) => ctx.costPriceList.length > 0,
    unavailableHintKey: 'reports.catalog.costprice.unavailable',
    Component: CostPriceReport,
  },
  {
    key: 'supplier',
    icon: Handshake,
    titleKey: 'reports.catalog.supplier.title',
    descriptionKey: 'reports.catalog.supplier.description',
    isAvailable: () => true,
    supportsAllRestaurants: true,
    Component: SupplierReport,
  },
  {
    key: 'inflation',
    icon: LineChart,
    titleKey: 'reports.catalog.inflation.title',
    descriptionKey: 'reports.catalog.inflation.description',
    // R5 availability depends on the selected period (orders in both the current
    // and previous period), which the hub doesn't track per-card — the report
    // itself shows an in-page explainer instead.
    isAvailable: () => true,
    supportsAllRestaurants: true,
    Component: InflationReport,
  },
  {
    key: 'inventory',
    icon: Package,
    titleKey: 'reports.catalog.inventory.title',
    descriptionKey: 'reports.catalog.inventory.description',
    isAvailable: (ctx) => ctx.inventorySessionsCount >= 1,
    hideWhenUnavailable: true,
    Component: InventoryReport,
  },
  {
    key: 'consumption',
    icon: Wine,
    titleKey: 'reports.catalog.consumption.title',
    descriptionKey: 'reports.catalog.consumption.description',
    isAvailable: (ctx) => ctx.hasConsumptionWindow,
    hideWhenUnavailable: true,
    Component: ConsumptionReport,
  },
];

export function getReportDefinition(key: string | undefined): ReportDefinition | undefined {
  return REPORTS.find(r => r.key === key);
}
